import ScrollableTabRow from "@/components/sales/dtools/ScrollableTabRow";
import { useProposalPremiumDisplay } from "@/components/proposal/ProposalPremiumDisplayContext";
import { PROPOSAL_COPY_FONT } from "@/lib/proposalTheme";
import {
  PROPOSAL_STICKY_TAB_TOP_CLASS,
  PROPOSAL_TAB_NAV_LOGO_PADDING_CLASS,
} from "@/lib/proposalCoverLayout";
import { cn } from "@/lib/utils";
import { useState } from "react";

export type ProposalSectionTab = {
  id: string;
  label: string;
};

type Props = {
  tabs: ProposalSectionTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  /** D-Tools JSON customer preview: stick tabs flush to the viewport top when scrolling. */
  stickyFlushTop?: boolean;
  "data-testid"?: string;
};

/** Sticky pill tab navigation shared by projector and retail premium proposals. */
export default function ProposalSectionTabNav({
  tabs,
  activeTab,
  onTabChange,
  stickyFlushTop = false,
  "data-testid": testId = "proposal-section-tabs",
}: Props) {
  const display = useProposalPremiumDisplay();
  const offsetBelowLogo = Boolean(display.fullViewportCover) && !stickyFlushTop;
  const reserveLogoPadding = Boolean(display.fullViewportCover);
  const [centerTabNonce, setCenterTabNonce] = useState(0);

  return (
    <nav
      className={cn(
        "sticky z-20 -mx-1 border-b border-[#1A2744]/10 bg-background/95 pl-1 pr-0 pb-3 pt-0 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:px-1",
        offsetBelowLogo ? PROPOSAL_STICKY_TAB_TOP_CLASS : "top-0",
      )}
      style={{ fontFamily: PROPOSAL_COPY_FONT }}
      aria-label="Proposal sections"
      data-testid={testId}
    >
      <ScrollableTabRow
        activeTabId={activeTab}
        centerActiveTabNonce={centerTabNonce}
        scrollAreaClassName={reserveLogoPadding ? PROPOSAL_TAB_NAV_LOGO_PADDING_CLASS : undefined}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            data-tab-id={tab.id}
            data-testid={`proposal-section-tab-${tab.id}`}
            className={cn(
              "shrink-0 rounded-full px-3.5 py-2 text-sm font-medium transition-colors whitespace-nowrap",
              activeTab === tab.id
                ? "bg-[#1A2744] text-white"
                : "max-w-[min(100vw-7rem,16rem)] truncate bg-muted/60 text-muted-foreground hover:bg-muted sm:max-w-[14rem]",
            )}
            onClick={() => {
              onTabChange(tab.id);
              setCenterTabNonce((n) => n + 1);
            }}
          >
            {tab.label}
          </button>
        ))}
      </ScrollableTabRow>
    </nav>
  );
}
